import { EventNames, lockHeader } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import { createBrowserHistory } from 'history';
import React from 'react';

import { getTranslation, keys } from '../../../Resources/Translations';
import { FirstFavouriteModal } from '../../Customers/Favourites/Components/FirstFavouriteModal/FirstFavouriteModal';
import { PriceFilterViewModel } from '../../Filtering/Api/PriceFilterViewModel.csharp';
import { ProductFilterResultViewModel } from '../../Filtering/Api/ProductFilterResultViewModel.csharp';
import { FavouritesListSortingType } from '../../Filtering/Sorting/FavouritesListSortingType.csharp';
import { ProductListSortingType } from '../../Filtering/Sorting/ProductListSortingType.csharp';
import { ProductSortingType } from '../../Filtering/Sorting/ProductSortingType.csharp';
import { SortingOptions } from '../../Filtering/Sorting/SortingOptions.csharp';
import { ProductCard } from '../../Partials/ProductCard/ProductCard';
import { ProductCardVariant } from '../../Partials/ProductCard/ProductCardVariant.csharp';
import { ProductCardViewModel } from '../../Partials/ProductCard/ProductCardViewModel.csharp';
import { clearSearchInput, ISearchEvent } from '../../Partials/Search/ClearSearchInput';
import { getFor } from '../../ReactComponents/API';
import { UserContextProvider } from '../../ReactComponents/Contexts/UserContext';
import { smoothScrollTo } from '../../ReactComponents/SmoothScrollTo';
import { RecommendedProducts } from '../../Recommendations/Components/RecommededProducts/RecommendedProducts';
import { GoogleTagManager } from '../../Tracking/GoogleTagManager/GoogleTagManager';
import { VisitorIntelligenceFacade } from '../../Tracking/VisitorIntelligence/VisitorIntelligenceFacade';
import { Input } from '../Common/Input';
import { Loader } from '../Loader/Loader';
import { PriceRangeValues } from '../PriceRange/PriceRange';
import { SelectedOptionList } from '../SelectedOptionList/SelectedOptionList';
import { LoadedTag, TagListProps } from '../TagList/TagList';
import { FilteredProductListProps } from './FilteredProductListProps.csharp';
import { FilterOptionViewModel } from './FilterOptionViewModel.csharp';
import { FilterViewModel } from './FilterViewModel.csharp';
import { PriceService } from './PriceService';
import { ProductFilters } from './ProductFilters';
import { SelectedOption } from './SelectedOption';

interface FilteredProductListState {
    productList: ProductCardViewModel[];
    numberOfProducts: number;
    filterList: FilterViewModel[];
    links: FilterViewModel;
    isLoading: boolean;
    searchPhrase: string;
    page: number;
    selectedPage: string;
    maxPage: number;
    priceRange: PriceRangeValues;
    sortingType: ProductSortingType;
    searchQuery?: string;
    resultsPerPage: number;
    hideReviewSnippet: boolean;
    isEmpty: boolean;
    loadedTags: LoadedTag[];
    tagsUsed: boolean;
}

export class FilteredProductList extends React.Component<FilteredProductListProps, FilteredProductListState> {
    private picturesWithoutLazyLoadingAmount = 8;
    private searchPhraseKey = 'searchPhrase';
    private lastTagKey = 'lastTag';
    private priceKey = 'priceFilter';
    private searchKey = 'searchPhrase';
    private readonly previousGaTrackedSearchPhrase: React.MutableRefObject<string | null> = React.createRef<string | null>();

    constructor(props: FilteredProductListProps) {
        super(props);

        this.previousGaTrackedSearchPhrase.current = props.results.searchPhrase;
        this.state = {
            productList: props.results.products,
            numberOfProducts: props.results.resultsCount,
            resultsPerPage: props.results.resultsPerPage,
            filterList: props.results.filters,
            links: props.results.links,
            isLoading: false,
            searchPhrase: props.results.searchPhrase,
            page: props.results.page,
            selectedPage: props.results.page.toString(),
            maxPage: props.results.maxPage,
            priceRange: this.buildPriceRange(props.results.priceFilter),
            sortingType: props.results.sortingType,
            hideReviewSnippet: this.hidePowerReviews(props.results.products),
            isEmpty: props.results.resultsCount == 0,
            loadedTags: [],
            tagsUsed: false
        };
    }

    componentDidMount() {
        window.addEventListener(EventNames.Search, this.search);
        this.setState({ searchQuery: window.location.search });

        const searchParams = new URLSearchParams(window.location.search);

        if (this.state.selectedPage !== '1') {
            searchParams.set('page', this.state.selectedPage);
        }

        const queryString = searchParams.toString();
        if (queryString) {
            history && window.history.replaceState(null, '', `?${queryString}`);
        }

        if (this.props.isProductListTrackable) {
            const searchPhrase = searchParams.get(this.searchPhraseKey) || '';

            VisitorIntelligenceFacade.TrackSearchResultsPage(
                searchPhrase,
                this.state.productList.map((p) => p.code),
                this.state.numberOfProducts
            );

            GoogleTagManager.viewSearchResults(searchPhrase, 'Classic');
        }
    }

    componentWillUnmount() {
        window.removeEventListener(EventNames.Search, this.search);
        window.removeEventListener('popstate', this.handleNavigationEvent);
    }

    componentDidUpdate(prevProps: Readonly<FilteredProductListProps>) {
        if (JSON.stringify(prevProps.allowedProducts) !== JSON.stringify(this.props.allowedProducts)) {
            const numberOfProducts =
                this.props.allowedProducts && this.state.numberOfProducts > this.props.allowedProducts?.length
                    ? this.props.allowedProducts.length
                    : this.state.numberOfProducts;

            this.setState({
                numberOfProducts,
                productList: this.filterAllowedProducts(this.state.productList),
                isEmpty: numberOfProducts == 0
            });
        }
    }

    private categoryPageBodyRef = React.createRef<HTMLDivElement>();
    private productSortingType = this.props.sortingOptions === SortingOptions.ForProductList ? ProductListSortingType : FavouritesListSortingType;
    private history = typeof window !== 'undefined' ? createBrowserHistory() : undefined;
    private priceService = PriceService(
        (priceRange: PriceRangeValues, callback?: () => void) => this.setState({ priceRange }, callback),
        (price: number[]) => {
            const query = this.buildQuery(this.state.searchPhrase, this.state.filterList, price);
            this.runQuery(query, this.state.page, this.state.sortingType);
        }
    );

    render() {
        const renderSortingOptions = (prefix: string) => {
            return (
                <label htmlFor={`sortingDropdown-${prefix}`} className={classNames('CategoryPage__SortingDropdown')}>
                    {<span>{getTranslation(keys['productFilters.sortBy'])}:</span>}
                    <div className="Form__FieldSelect">
                        <select
                            id={`sortingDropdown-${prefix}`}
                            value={this.state.sortingType}
                            onChange={(e) => this.handleSortingTypeChange(Number(e.target.value))}
                        >
                            {Object.values(this.productSortingType)
                                .map((key) => Number(key))
                                .filter((key) => !isNaN(key))
                                .map((key) => {
                                    const translationKey = (keys as unknown as Record<string, string>)[`sorting.${this.productSortingType[key]}`];
                                    return (
                                        <option value={key} key={'sorting_' + key}>
                                            {getTranslation(translationKey)}
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                </label>
            );
        };

        const searchPhraseFilter = { filterKey: this.searchKey, optionKey: this.searchKey, value: `"${this.state.searchPhrase}"` };

        return (
            <UserContextProvider>
                <FirstFavouriteModal favouritesPageUrl={this.props.favouritesPageUrl} />
                <div className="CategoryPage__Body" ref={this.categoryPageBodyRef}>
                    <Loader
                        isLoading={this.state.isLoading}
                        className={classNames('CategoryPage__Loader', { 'CategoryPage__Loader--OnTop': !this.state.productList?.length })}
                    >
                        {!this.state.isEmpty ? (
                            <>
                                <div className="CategoryPage__Sidebar">
                                    {this.props.showFilters && (
                                        <ProductFilters
                                            links={this.state.links}
                                            filters={this.state.filterList}
                                            searchQuery={this.state.searchQuery ?? ''}
                                            selectedOptions={this.buildSelectedOptions()}
                                            productAmount={this.state.numberOfProducts || 0}
                                            priceRange={this.state.priceRange}
                                            onChangePriceRange={(priceValues: number[]) => this.priceService.onChangeRange(priceValues, this.state.priceRange)}
                                            onChangePriceInputMin={(value: string) => this.priceService.onChangeInputMin(value, this.state.priceRange)}
                                            onChangePriceInputMax={(value: string) => this.priceService.onChangeInputMax(value, this.state.priceRange)}
                                            onAfterChangePrice={() => this.priceService.onAfterChange(this.state.priceRange)}
                                            currencyCode={this.props.results.priceFilter.currencyCode}
                                            onChangeOption={this.toggleOption}
                                            onReset={() => this.resetFilters(this.state.filterList)}
                                            link={this.props.content}
                                            loadedTags={[]}
                                        />
                                    )}

                                    <div className="CategoryPage__SortingSidebar">{renderSortingOptions('mobile')}</div>
                                </div>
                                <div className={classNames('CategoryPage__Products', this.props.showFilters && 'CategoryPage__Products--WithFilters')}>
                                    <div className="CategoryPage__Sorting">
                                        <div className="CategoryPage__ResultsAmountWrapper">
                                            {this.state.searchPhrase ? (
                                                <>
                                                    <span className="CategoryPage__ResultsAmount">
                                                        {this.state.numberOfProducts || 0} {getTranslation(keys['productFilters.searchResults'])}
                                                    </span>
                                                    <span className="CategoryPage__SearchPhraseWrapper">
                                                        &nbsp;&#34;<span className="CategoryPage__SearchPhrase">{this.state.searchPhrase}</span>&#34;
                                                    </span>
                                                    <button
                                                        className="SearchPage__Remove"
                                                        onClick={() => this.resetOption(searchPhraseFilter.filterKey, '', searchPhraseFilter.optionKey, false)}
                                                    >
                                                        <span className="visuallyHidden">{getTranslation(keys['cartSummary.remove'])}</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="CategoryPage__ResultsAmount">
                                                    {this.state.numberOfProducts || 0} {getTranslation(keys['productFilters.products'])}
                                                </span>
                                            )}
                                        </div>
                                        {renderSortingOptions('desktop')}
                                    </div>

                                    {this.buildSelectedOptions().length > 0 && (
                                        <SelectedOptionList
                                            selectedOptions={this.buildSelectedOptions()}
                                            onRemoveOption={(filterKey: string, subfilterKey: string | null, optionKey: string, isSelected: boolean) =>
                                                this.resetOption(filterKey, subfilterKey, optionKey, isSelected)
                                            }
                                            onReset={() => this.resetFilters(this.state.filterList)}
                                        />
                                    )}
                                    <div className="CategoryPage__ListWrapper">
                                        {this.state.productList && this.state.productList.length > 0 && (
                                            <ul className="CategoryPage__List">
                                                {this.state.productList.map((product, index) => (
                                                    <li className="CategoryPage__ListItem" key={product.code}>
                                                        <ProductCard
                                                            variant={ProductCardVariant.Vertical}
                                                            profile={this.props.profile}
                                                            cartItemUrl={this.props.cartItemUrl}
                                                            validateItemUrl={this.props.validateItemUrl}
                                                            model={product}
                                                            showFavouritesButton={true}
                                                            rxUrl={this.props.rxUrl}
                                                            isLazyLoading={index >= this.picturesWithoutLazyLoadingAmount}
                                                            favouritesUrl={this.props.favouritesUrl}
                                                            sortingType={this.state.sortingType}
                                                            experimentProductClickUrl={this.props.experimentProductClickUrl}
                                                            epiFindClickTrackingUrl={this.props.epiFindClickTrackingUrl}
                                                            hidePowerReviews={this.state.hideReviewSnippet}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {this.state.maxPage > 1 && (
                                            <ul className="Paging">
                                                {this.state.page != 1 && (
                                                    <li>
                                                        <button className="Paging_Button Paging__First" onClick={() => this.changePage(this.state.page - 1)}>
                                                            <span className="visuallyHidden">{getTranslation(keys['paging.previousPage'])}</span>
                                                        </button>
                                                    </li>
                                                )}
                                                <li className="Paging__PageNumber">
                                                    <label className="visuallyHidden" htmlFor="pageInput">
                                                        {getTranslation(keys['paging.currentPage'])}
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        value={this.state.selectedPage}
                                                        id="pageInput"
                                                        onChange={(value) => this.setState({ selectedPage: value })}
                                                        onChangeCondition={(value: string) => {
                                                            const selectedPage = Number(value);
                                                            return !value || (!isNaN(selectedPage) && selectedPage >= 1 && selectedPage <= this.state.maxPage);
                                                        }}
                                                        onAfterChange={() => this.handlePageChange(this.state.selectedPage)}
                                                    />
                                                    {getTranslation(keys['paging.of'])} {this.state.maxPage}
                                                </li>
                                                {this.state.page != this.state.maxPage && (
                                                    <li>
                                                        <button className="Paging_Button Paging__Last" onClick={() => this.changePage(this.state.page + 1)}>
                                                            <span className="visuallyHidden">{getTranslation(keys['paging.nextPage'])}</span>
                                                        </button>
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            this.props.displayEmptyPageInformation && (
                                <div className="SearchEmpty">
                                    <span className="SearchEmpty__Heading">{getTranslation(keys['search.noResults'])}</span>
                                    {this.props.searchRecommendationsProps ? (
                                        <>
                                            <span className="SearchEmpty__SubHeading">{getTranslation(keys['search.seeRecommendations'])}</span>
                                            <RecommendedProducts
                                                variant={this.props.searchRecommendationsProps.variant}
                                                variantMobile={this.props.searchRecommendationsProps.variantMobile}
                                                productsUrl={this.props.searchRecommendationsProps.productsUrl}
                                                epiFindClickTrackingUrl={this.props.searchRecommendationsProps.epiFindClickTrackingUrl}
                                                experimentProductClickUrl={this.props.searchRecommendationsProps.experimentProductClickUrl}
                                                profile={this.props.searchRecommendationsProps.profile}
                                                cartItemUrl={this.props.searchRecommendationsProps.cartItemUrl}
                                                validateItemUrl={this.props.searchRecommendationsProps.validateItemUrl}
                                                rxUrl={this.props.searchRecommendationsProps.rxUrl}
                                                model={this.props.searchRecommendationsProps.model}
                                            />
                                        </>
                                    ) : (
                                        <span className="SearchEmpty__SubHeading">{getTranslation(keys['search.checkSpelling'])}</span>
                                    )}
                                </div>
                            )
                        )}
                    </Loader>
                </div>
            </UserContextProvider>
        );
    }

    private buildTagListProps(): TagListProps {
        return {
            onClick: this.toggleTag,
            leadInText: this.props.leadInText,
            selectedOptions: this.buildSelectedOptions(),
            onAddOption: (filterKey: string, subfilterKey: string | null, optionKey: string, isSelected: boolean) => {
                this.resetOption(filterKey, subfilterKey, optionKey, isSelected);
            },
            loadedTags: this.state.loadedTags
        };
    }

    private buildPriceRange(priceFilter: PriceFilterViewModel): PriceRangeValues {
        const selectedPrice = [priceFilter.selectedMinPrice, priceFilter.selectedMaxPrice];
        const thresholds = [priceFilter.minPrice, priceFilter.maxPrice];
        const displayedPrice = [
            selectedPrice[0] < thresholds[0] ? thresholds[0] : selectedPrice[0],
            selectedPrice[1] > thresholds[1] ? thresholds[1] : selectedPrice[1]
        ];

        return {
            input: displayedPrice.map((x) => x.toString()),
            selected: selectedPrice,
            slider: displayedPrice,
            thresholds: thresholds
        };
    }

    private clearFilters = (filters: FilterViewModel[]) => {
        const updatedFilters = [...filters];

        for (const filter of updatedFilters) {
            filter.options = filter.options.map((option) => {
                option.isSelected = false;

                if (option.children) {
                    option.children = option.children.map((subOption) => {
                        subOption.isSelected = false;
                        return subOption;
                    });
                }

                return option;
            });
        }
        return updatedFilters;
    };

    private resetFilters = (filters: FilterViewModel[]) => {
        const updatedFilters = this.clearFilters(filters);

        const query = this.buildQuery(this.state.searchPhrase, updatedFilters, this.state.priceRange.thresholds);
        this.runQuery(query, this.state.page, this.state.sortingType);
    };

    private resetOption = (filterKey: string, subfilterKey: string | null, optionKey: string, isSelected: boolean) => {
        if (optionKey === this.priceKey) {
            this.resetPrice();
            return;
        }

        if (optionKey === this.searchKey) {
            this.resetSearch();
            return;
        }

        this.toggleOption(filterKey, subfilterKey, optionKey, isSelected, this.isCurrentOptionASubfilter(filterKey, subfilterKey, optionKey));
    };

    private resetSearch = () => {
        this.setState({ searchPhrase: '', tagsUsed: false });
        clearSearchInput();
        const query = this.buildQuery('', this.state.filterList, this.state.priceRange.thresholds);
        this.runQuery(query, 1, this.state.sortingType);
    };

    private isCurrentOptionASubfilter = (filterKey: string, subfilterKey: string | null, optionKey: string): boolean => {
        const filter = this.state.filterList.find((x) => x.key === filterKey);
        if (filter && subfilterKey && filter.subOptionKey === subfilterKey) {
            return !!filter.options.find((x) => x.children.some((x) => x.key === optionKey));
        }

        return false;
    };

    private resetPrice = () => {
        const query = this.buildQuery(this.state.searchPhrase, this.state.filterList, this.state.priceRange.thresholds);
        this.runQuery(query, 1, this.state.sortingType);
    };

    private toggleTag = (selectedTag: string, removeSearchQuery: boolean, filters: { key: string; value: string }[], callback?: () => void) => {
        let searchPhrase = this.state.searchPhrase;
        if (removeSearchQuery) {
            this.setState({ searchPhrase: '' });
            clearSearchInput();
            searchPhrase = '';
        }

        let query: string;
        if (this.state.isEmpty) {
            const groupedFilters = filters.reduce((accumulator: { [key: string]: string[] }, filter) => {
                if (!(filter.key in accumulator)) {
                    accumulator[filter.key] = [];
                }

                accumulator[filter.key].push(filter.value);

                return accumulator;
            }, {});

            const queryParams = new URLSearchParams();
            for (const key in groupedFilters) {
                queryParams.set(key, groupedFilters[key].join(','));
            }
            queryParams.set(this.searchPhraseKey, searchPhrase);
            query = queryParams.toString();
        } else {
            const updatedFilters: FilterViewModel[] | undefined = [];

            filters.map((filter) => {
                const parent = this.getParentOption(filter.key, filter.value);
                const builtFilters = parent
                    ? this.buildUpdatedFilters(parent.key, parent.value, filter.value, true, true)
                    : this.buildUpdatedFilters(filter.key, null, filter.value, true);

                if (!builtFilters) return;
                updatedFilters.push(...builtFilters);
            });

            if (!updatedFilters) return;

            query = this.buildQuery(searchPhrase, updatedFilters, this.state.priceRange.selected, selectedTag);
        }

        this.runQuery(query, 1, this.state.sortingType, undefined, callback, true);
    };

    private buildUpdatedFilters(
        filterKey: string,
        subfilterKey: string | null,
        optionKey: string,
        isSelected: boolean,
        isSubfilter?: boolean
    ): FilterViewModel[] | undefined {
        let updatedFilters: FilterViewModel[];

        if (subfilterKey && isSubfilter) {
            const filters = this.toggleWithSubfilter(filterKey, subfilterKey, optionKey, isSelected);
            if (!filters) return;

            updatedFilters = filters;
        } else {
            const filters = this.toggleWithoutSubfilter(filterKey, optionKey, isSelected);
            if (!filters) return;

            updatedFilters = filters;
        }

        return updatedFilters;
    }

    private toggleOption = (
        filterKey: string,
        subfilterKey: string | null,
        optionKey: string,
        isSelected: boolean,
        isSubfilter?: boolean,
        callback?: () => void
    ) => {
        const updatedFilters = this.buildUpdatedFilters(filterKey, subfilterKey, optionKey, isSelected, isSubfilter);
        if (!updatedFilters) return;

        const query = this.buildQuery(this.state.searchPhrase, updatedFilters, this.state.priceRange.selected);
        this.runQuery(query, 1, this.state.sortingType, undefined, callback);
    };

    private toggleWithSubfilter = (filterKey: string, subfilterKey: string, optionKey: string, isSelected: boolean): FilterViewModel[] | undefined => {
        const filterIndex = this.state.filterList.findIndex((filter) => filter.key === filterKey);
        if (filterIndex < 0) return;

        const updatedFilters = [...this.state.filterList];

        const options = this.state.filterList[filterIndex].options;
        for (const option of options) {
            let selected = false;

            for (const subOption of option.children) {
                if (subOption.key === optionKey) {
                    subOption.isSelected = selected = isSelected;
                    continue;
                }

                subOption.isSelected = false;
            }

            option.isSelected = selected;
        }

        return updatedFilters;
    };

    private toggleWithoutSubfilter = (filterKey: string, optionKey: string, isSelected: boolean): FilterViewModel[] | undefined => {
        const filterIndex = this.state.filterList.findIndex((filter) => filter.key === filterKey);
        if (filterIndex < 0) return;

        const optionIndex = this.state.filterList[filterIndex].options.findIndex((option) => option.key === optionKey);
        if (optionIndex < 0) return;

        const updatedFilters = filterKey === 'category' ? this.updateFiltersForCategory(filterIndex) : [...this.state.filterList];
        const option = updatedFilters[filterIndex].options[optionIndex];
        option.isSelected = isSelected;

        return updatedFilters;
    };

    private updateFiltersForCategory = (filterIndex: number): FilterViewModel[] => {
        const updatedFilters = [...this.state.filterList];

        updatedFilters[filterIndex].options = updatedFilters[filterIndex].options.map((option) => {
            option.isSelected = false;
            option.children = option.children.map((x) => {
                return { ...x, isSelected: false };
            });
            return option;
        });

        return updatedFilters;
    };

    private handleSortingTypeChange = (value: number) => {
        const { searchPhrase, filterList, priceRange } = this.state;
        const query = this.buildQuery(searchPhrase, filterList, priceRange.selected);
        this.runQuery(query, 1, value);
    };

    private buildSelectedOptions = (): SelectedOption[] => {
        const selectedOptions = this.state.filterList.map((f) => this.getSelectedOptions(f)).reduce((a, b) => a.concat(b), []);
        if (this.isActivePriceFilter()) {
            selectedOptions.push({ filterKey: this.priceKey, subFilterKey: '', optionKey: this.priceKey, value: this.getPriceFilter() } as SelectedOption);
        }

        return this.props.brandId ? selectedOptions.filter((x) => x.filterKey !== 'brand') : selectedOptions;
    };

    private getPriceFilter = () => `${this.state.priceRange.selected[0]} - ${this.state.priceRange.selected[1]} ${this.props.results.priceFilter.currencyCode}`;

    private isActivePriceFilter = () =>
        this.state.priceRange.thresholds[0] != this.state.priceRange.selected[0] || this.state.priceRange.thresholds[1] != this.state.priceRange.selected[1];

    private getSelectedOptions = (filter: FilterViewModel): SelectedOption[] => {
        const isSubFilter = this.isSubfilter(filter);
        const options = this.getOptions(filter, isSubFilter);
        return options.map((x) => ({ filterKey: filter.key, subFilterKey: filter.subOptionKey, optionKey: x.key, value: x.value, isSubFilter }));
    };

    private runQuery = (query: string, page: number, sortingType: number, scrollToTop?: boolean, callback?: () => void, tagsUsed = false) => {
        const queryString = `${query}&orderBy=${sortingType}`;

        if (this.history) {
            const searchQuery = `?${queryString}${page !== 1 ? `&page=${page}` : ''}`;
            this.history.push(searchQuery);
            this.setState({ searchQuery });
        }

        this.queryForProducts(`${queryString}&page=${page}`, scrollToTop, callback, tagsUsed);
    };

    private hidePowerReviews = (products: ProductCardViewModel[]): boolean => {
        return products.every((p) => p.hidePowerReviews || !p.powerReviewsRating);
    };

    private queryForProducts = (query: string, scrollToTop?: boolean, callback?: () => void, tagsUsed = false) => {
        let url = `${this.props.productsUrl}?`;
        if (this.props.content) {
            url += `id=${this.props.content}&`;
        } else if (this.props.brandId) {
            url += `brand=${this.props.brandId}&`;
        }

        url += `&${query}`;

        this.setState({ isLoading: true, tagsUsed: tagsUsed });

        getFor<ProductFilterResultViewModel>(url)
            .then((result) => {
                if (scrollToTop) {
                    lockHeader(true, true);
                    smoothScrollTo(this.getTop(), () => {
                        this.setResults(result);
                        window.scrollTo(0, this.getTop());
                        this.setPopstateEventListener();
                        setTimeout(() => {
                            lockHeader(false);
                        }, 100);
                    });
                } else {
                    this.setResults(result);
                    this.setPopstateEventListener();
                }

                const searchPhrase = new URLSearchParams(url).get(this.searchPhraseKey) || '';
                if (this.props.isProductListTrackable) {
                    VisitorIntelligenceFacade.TrackSearchResultsPage(
                        searchPhrase,
                        result.products.map((p) => p.code),
                        result.products.length
                    );

                    if (searchPhrase !== this.previousGaTrackedSearchPhrase.current) {
                        GoogleTagManager.viewSearchResults(searchPhrase, 'Classic');
                        this.previousGaTrackedSearchPhrase.current = searchPhrase;
                    }
                }

                callback && callback();
            })
            .catch(() => {
                this.setEmptyResults();
            });
    };

    private setPopstateEventListener = () => {
        window.addEventListener('popstate', this.handleNavigationEvent);
    };

    private handleNavigationEvent = () => {
        this.queryForProducts(window.location.search.replace('?', ''), true);
        const searchParams = new URLSearchParams(window.location.search);
        this.setState({ searchPhrase: searchParams.get(this.searchPhraseKey) || '' });
    };

    private setResults = (result: ProductFilterResultViewModel) => {
        const numberOfProducts =
            this.props.allowedProducts && result.resultsCount > this.props.allowedProducts?.length ? this.props.allowedProducts.length : result.resultsCount;

        this.setState({
            productList: this.filterAllowedProducts(result.products),
            filterList: result.filters,
            links: result.links,
            page: result.page,
            selectedPage: result.page.toString(),
            maxPage: result.maxPage,
            numberOfProducts,
            isLoading: false,
            priceRange: this.buildPriceRange(result.priceFilter),
            sortingType: result.sortingType,
            hideReviewSnippet: this.hidePowerReviews(result.products),
            isEmpty: numberOfProducts == 0
        });
    };

    private setEmptyResults = () => {
        this.setResults({
            products: [],
            filters: [
                {
                    key: 'category',
                    label: 'Kategori',
                    subOptionKey: 'subcategory',
                    options: [],
                    // eslint-disable-next-line
                    link: null!,
                    // eslint-disable-next-line
                    url: null!
                }
            ],
            // eslint-disable-next-line
            links: null!,
            page: 1,
            maxPage: 1,
            resultsPerPage: 0,
            resultsCount: 0,
            priceFilter: {
                minPrice: 0,
                maxPrice: 0,
                selectedMinPrice: 0,
                selectedMaxPrice: 0,
                currencyCode: 'kr'
            },
            searchPhrase: this.state.searchPhrase,
            sortingType: this.state.sortingType
        });
    };

    private filterAllowedProducts = (productList: ProductCardViewModel[]) => {
        // eslint-disable-next-line
        return this.props.allowedProducts ? productList.filter((x) => this.props.allowedProducts!.includes(x.code)) : productList;
    };

    private getTop = (): number => {
        // spacing between header and CategoryPage__Body after auto-scroll
        const additionalPaddingFromTop = 20;

        const headerHeight = this.getHeaderHeight();
        const offsetTop = headerHeight + additionalPaddingFromTop;

        return this.categoryPageBodyRef.current ? this.categoryPageBodyRef.current.offsetTop - offsetTop : 0;
    };

    private getHeaderHeight = (): number => {
        const defaultHeaderHeight = 88;
        const header = document?.querySelector('.Header__Top') as HTMLElement;
        return header?.offsetHeight || defaultHeaderHeight;
    };

    private buildQuery = (searchPhrase: string, filters: FilterViewModel[], priceValues: number[], lastTag = ''): string => {
        const searchParams = new URLSearchParams();
        if (searchPhrase) {
            searchParams.set(this.searchPhraseKey, searchPhrase);
        }

        for (const filter of filters) {
            let optionQuery = '';

            const options = this.getOptions(filter, this.isSubfilter(filter));

            for (const option of options) {
                optionQuery += optionQuery.length > 0 ? `,${option.key}` : option.key;
            }

            if (optionQuery.length > 0) {
                searchParams.set(filter.subOptionKey && this.isSubfilter(filter) ? filter.subOptionKey : filter.key, optionQuery);
            }
        }

        if (this.state.priceRange.thresholds[0] != priceValues[0] || this.state.priceRange.thresholds[1] != priceValues[1]) {
            searchParams.set('minPrice', priceValues[0].toString());
            searchParams.set('maxPrice', priceValues[1].toString());
        }

        if (lastTag) {
            searchParams.set(this.lastTagKey, lastTag);
        }

        return searchParams.toString() || '';
    };

    private isSubfilter = (filter: FilterViewModel) => {
        return filter.options.filter((filterOption) => filterOption.children?.filter((subfilterOption) => subfilterOption.isSelected).length > 0).length > 0;
    };

    private getParentOption = (key: string, value: string): { key: string; value: string } | undefined => {
        const filterIndex = this.state.filterList.findIndex((filter) => filter.subOptionKey === key);
        if (filterIndex < 0) {
            return;
        }

        const option = this.state.filterList[filterIndex].options.flatMap((option) => option.children).find((child) => child.key === value);
        if (!option) {
            return;
        }

        return { key: this.state.filterList[filterIndex].key, value: option?.parentKey };
    };

    private getOptions = (filter: FilterViewModel, isSubfilter?: boolean): FilterOptionViewModel[] => {
        return filter.subOptionKey && isSubfilter
            ? filter.options
                  .map((o) => o.children)
                  .reduce((a, b) => {
                      return a.concat(b);
                  }, [])
                  .filter((x) => x.isSelected)
            : filter.options.filter((x) => x.isSelected);
    };

    private changePage = (selectedPage: number) => {
        if (selectedPage >= 1 && selectedPage <= this.state.maxPage) {
            this.runQueryForPage(selectedPage, true);
        }
    };

    private runQueryForPage = (selectedPage: number, scrollToTop?: boolean) => {
        const query = this.buildQuery(this.state.searchPhrase, this.state.filterList, this.state.priceRange.selected);
        this.runQuery(query, selectedPage, this.state.sortingType, scrollToTop);
    };

    private handlePageChange = (value: string) => {
        if (!value) {
            this.setState({ selectedPage: this.state.page.toString() });
            return;
        }

        const selectedPage = Number(value);
        if (isNaN(selectedPage) || selectedPage < 1 || selectedPage > this.state.maxPage) {
            this.setState({ selectedPage: this.state.page.toString() });
            return;
        }

        if (selectedPage !== this.state.page) {
            this.runQueryForPage(selectedPage);
        }
    };

    private search = ($event: CustomEventInit) => {
        const search = $event?.detail as ISearchEvent;
        if (!search) {
            return;
        }

        if (search.phrase !== this.state.searchPhrase) {
            this.clearFilters(this.state.filterList);
        }
        this.setState({ searchPhrase: search.phrase });
        const query = this.buildQuery(search.phrase, this.state.filterList, this.state.priceRange.selected);
        this.runQuery(query, 1, this.state.sortingType);
    };
}
